import { formatDate, formatDayMonth } from "../../api/dates";
import { formatMoney } from "../../api/money";
import type {
  JobBoqRef,
  JobCustomerOrderRef,
  JobDraftBoqRef,
  JobInvoiceRef,
  JobOrderRef,
  Queue,
  QuotationDetail,
  QuotationRow,
  QuotationStatus,
} from "../../api/quotations";
import { boqLabel, customerOrderLabel, orderLabel, quotationLabel } from "./tones";

/** The six documents of a job, in the order the work produces them. They
 * replace the six tabs (Fix #11): a tab strip said what could be opened, a
 * pipeline says what is done, what is waiting and where the job is.
 *
 * The draft BOQ comes first: the office prices the job by hand before the
 * supplier quotes, and the customer BOQ is a copy of it, amended for any
 * additional work. A job without one is not held up by it. */
export type StepKey = "draft" | "quote" | "po" | "boq" | "customer" | "invoice";

export const STEPS: readonly StepKey[] = ["draft", "quote", "po", "boq", "customer", "invoice"];

/** A supplier is shown its own two documents and nothing of the City's. */
export const SUPPLIER_STEPS: readonly StepKey[] = ["quote", "po"];

export const STEP_LABEL: Record<StepKey, string> = {
  draft: "Draft BOQ",
  quote: "Supplier quote",
  po: "Supplier PO",
  boq: "Customer BOQ",
  customer: "Customer PO",
  invoice: "Invoice",
};

/** `?tab=` values the old tab strip wrote, still honoured so a link sent last
 * week opens the same document: "order" was the quotation's own tab. */
export function stepFromParam(value: string | null): StepKey | null {
  if (value === "order") {
    return "quote";
  }
  return value !== null && (STEPS as readonly string[]).includes(value) ? (value as StepKey) : null;
}

/** done: finished and it counted; current: open, or where the job is; todo:
 * not started; stopped: finished without effect (rejected, cancelled). */
export type StepState = "done" | "current" | "todo" | "stopped";

export interface StepView {
  key: StepKey;
  label: string;
  state: StepState;
  /** One line under the label: where the document stands, and when. */
  sub: string;
}

/** What a row and a detail both know about a job, which is all the pipeline
 * needs -- so the rail row's little segments and the job's own steps cannot
 * disagree. */
export interface JobFacts {
  status: QuotationStatus;
  queue: Queue;
  decidedAt: string | null;
  uncovered: number;
  draftBoq: JobDraftBoqRef | null;
  order: JobOrderRef | null;
  boq: JobBoqRef | null;
  customerOrder: JobCustomerOrderRef | null;
  invoice: JobInvoiceRef | null;
}

export function factsOfRow(row: QuotationRow): JobFacts {
  return {
    status: row.status,
    queue: row.queue,
    decidedAt: null,
    uncovered: 0,
    draftBoq: row.draft_boq ?? null,
    order: row.order,
    boq: row.boq,
    customerOrder: row.customer_order,
    invoice: row.invoice,
  };
}

export function factsOfDetail(detail: QuotationDetail): JobFacts {
  return {
    status: detail.status,
    queue: detail.queue,
    decidedAt: detail.decision?.at ?? null,
    uncovered: detail.uncovered.length,
    draftBoq: detail.draft_boq ?? null,
    order: detail.supplier_order,
    boq: detail.boq,
    customerOrder: detail.customer_order ?? null,
    invoice: detail.invoice ?? null,
  };
}

function dated(text: string, value: string | null | undefined): string {
  return value ? `${text} ${formatDayMonth(value)}` : text;
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/** Step 1 never holds a job up: a job without a draft says so and moves on. */
function draftStep(facts: JobFacts): Omit<StepView, "key" | "label"> {
  const draft = facts.draftBoq;
  if (draft === null) {
    return { state: "todo", sub: "None" };
  }
  if (draft.line_count === 0) {
    return { state: "current", sub: "Being priced" };
  }
  const total = formatMoney(draft.total_excl_vat);
  return { state: "done", sub: draft.copied ? `${total} · Copied` : total };
}

function quoteStep(facts: JobFacts): Omit<StepView, "key" | "label"> {
  switch (facts.status) {
    case "approved":
      return { state: "done", sub: dated("Approved", facts.decidedAt) };
    case "rejected":
    case "superseded":
      return { state: "stopped", sub: dated(quotationLabel(facts.status), facts.decidedAt) };
    case "returned":
      return { state: "current", sub: "Returned to the supplier" };
    case "draft":
      return {
        state: "current",
        sub: facts.uncovered > 0 ? `Draft · ${facts.uncovered} ${plural(facts.uncovered, "part", "parts")} uncovered` : "Draft",
      };
    default:
      return { state: "current", sub: quotationLabel(facts.status) };
  }
}

function orderStep(facts: JobFacts): Omit<StepView, "key" | "label"> {
  const order = facts.order;
  if (order === null) {
    return { state: facts.status === "approved" ? "current" : "todo", sub: "Not raised" };
  }
  switch (order.status) {
    case "acknowledged":
      return { state: "done", sub: dated(`${order.number} · Acknowledged`, order.acknowledged_at) };
    case "issued":
      return { state: "current", sub: dated(`${order.number} · Issued`, order.issued_at) };
    case "sent":
      return { state: "current", sub: dated(`${order.number} · Sent`, order.sent_at) };
    case "cancelled":
      return { state: "stopped", sub: `${order.number} · Cancelled` };
    default:
      return {
        state: "current",
        sub:
          order.unpriced > 0
            ? `${order.unpriced} ${plural(order.unpriced, "line", "lines")} unpriced`
            : `${order.number} · ${orderLabel(order.status)}`,
      };
  }
}

function boqStep(facts: JobFacts): Omit<StepView, "key" | "label"> {
  const boq = facts.boq;
  if (boq === null) {
    return { state: facts.queue === "bill" ? "current" : "todo", sub: facts.status === "approved" ? "Not raised" : "—" };
  }
  if (boq.status === "approved") {
    return { state: "done", sub: dated("Approved", boq.approved_at) };
  }
  if (boq.status === "rejected" || boq.status === "superseded") {
    return { state: "stopped", sub: boqLabel(boq.status) };
  }
  if (boq.status === "submitted") {
    return { state: "current", sub: dated("Issued", boq.issued_at) };
  }
  return { state: "current", sub: boqLabel(boq.status) };
}

function customerStep(facts: JobFacts): Omit<StepView, "key" | "label"> {
  const order = facts.customerOrder;
  if (order !== null) {
    if (order.status === "validated" || order.status === "closed") {
      return { state: "done", sub: dated(order.number, order.order_date) };
    }
    if (order.status === "rejected" || order.status === "cancelled") {
      return { state: "stopped", sub: `${order.number} · ${customerOrderLabel(order.status)}` };
    }
    return { state: "current", sub: `${order.number} · ${customerOrderLabel(order.status)}` };
  }
  if ((facts.boq?.status === "submitted" || facts.boq?.status === "approved") && facts.invoice === null) {
    return { state: "current", sub: "Waiting for the City" };
  }
  return { state: "todo", sub: "—" };
}

function invoiceStep(facts: JobFacts): Omit<StepView, "key" | "label"> {
  if (facts.invoice !== null) {
    return { state: "done", sub: dated(facts.invoice.number, facts.invoice.invoice_date) };
  }
  if (facts.customerOrder?.status === "validated") {
    return { state: "current", sub: "Due" };
  }
  return { state: "todo", sub: "—" };
}

const STEP_OF: Record<StepKey, (facts: JobFacts) => Omit<StepView, "key" | "label">> = {
  draft: draftStep,
  quote: quoteStep,
  po: orderStep,
  boq: boqStep,
  customer: customerStep,
  invoice: invoiceStep,
};

/** Every step of the job, each with its state and its line. */
export function stepsOf(facts: JobFacts, keys: readonly StepKey[] = STEPS): StepView[] {
  return keys.map((key) => ({ key, label: STEP_LABEL[key], ...STEP_OF[key](facts) }));
}

// ---------------------------------------------------------------- next step

export type NextAction =
  | { kind: "goto"; label: string; step: StepKey; read?: boolean }
  | { kind: "raise-boq"; label: string }
  | { kind: "issue-order"; label: string; orderId: number }
  | { kind: "issue-invoice"; label: string; boqId: number };

export interface NextStep {
  /** One sentence: what is outstanding and who has it. */
  text: string;
  /** The document the job opens on, and the one the sentence is about. */
  step: StepKey;
  action: NextAction | null;
  done: boolean;
}

function supplierName(detail: QuotationDetail): string {
  return detail.supplier?.name ?? "the supplier";
}

function lastNote(detail: QuotationDetail, statuses: QuotationStatus[]): { at: string | null; note: string | null } | null {
  const found = detail.history.find((row) => statuses.includes(row.status));
  return found === undefined ? null : { at: found.at, note: found.note };
}

function capturing(detail: QuotationDetail): NextStep {
  const open: NextAction = { kind: "goto", label: "Open the quote", step: "quote" };
  if (detail.status === "returned") {
    const returned = lastNote(detail, ["returned"]);
    const why = returned?.note ? `: “${returned.note}”` : "";
    // Reopened by cancelling its order: the office is revising it, not the supplier.
    if (detail.supplier_order?.status === "cancelled") {
      return {
        text: `Reopened for revision${why}. Edit, add or remove lines, then resubmit and approve it to raise the replacement order.`,
        step: "quote",
        action: open,
        done: false,
      };
    }
    return { text: `Returned to ${supplierName(detail)}${why}. Waiting for their revision.`, step: "quote", action: open, done: false };
  }
  const gaps = detail.uncovered.length;
  const uncovered = gaps === 0 ? "" : gaps === 1 ? " One section 9 part has no line." : ` ${gaps} section 9 parts have no line.`;
  const lapsed = detail.expired && detail.valid_until ? ` The quote's validity lapsed on ${formatDate(detail.valid_until)}.` : "";
  return {
    text: `Still being captured.${uncovered}${lapsed} Submit it for approval once the lines are right.`,
    step: "quote",
    action: open,
    done: false,
  };
}

function approving(detail: QuotationDetail): NextStep {
  const submitted = lastNote(detail, ["submitted", "resubmitted"]);
  const when = submitted?.at ? ` on ${formatDate(submitted.at)}` : "";
  const again = detail.status === "resubmitted" ? "Resubmitted" : "Submitted";
  return {
    text: `${again} for approval${when}. Check the lines and approve, return or reject it.`,
    step: "quote",
    action: { kind: "goto", label: "Review the quote", step: "quote" },
    done: false,
  };
}

function pricing(detail: QuotationDetail): NextStep {
  const order = detail.supplier_order;
  if (order === null) {
    const lapsed = detail.expired ? " The quoted prices had lapsed, so the order waits for somebody to say why they still hold." : "";
    return { text: `Approved, but no order has been raised yet.${lapsed}`, step: "po", action: null, done: false };
  }
  if (order.unpriced > 0) {
    const count = order.unpriced;
    return {
      text: `${count === 1 ? "One line" : `${count} lines`} on ${order.number} ${plural(count, "has", "have")} no price yet. Price ${plural(count, "it", "them")}, then issue the order to ${supplierName(detail)}.`,
      step: "po",
      action: { kind: "goto", label: "Price the lines", step: "po" },
      done: false,
    };
  }
  return {
    text: `Every line on ${order.number} is priced. Issue it to ${supplierName(detail)}.`,
    step: "po",
    action: { kind: "issue-order", label: `Issue to ${supplierName(detail)}`, orderId: order.id },
    done: false,
  };
}

function billing(detail: QuotationDetail): NextStep {
  const boq = detail.boq;
  const order = detail.supplier_order;
  if (boq === null) {
    if (detail.boq_blocker) {
      return { text: detail.boq_blocker, step: "boq", action: null, done: false };
    }
    const acked = order ? `${supplierName(detail)} acknowledged ${order.number}. ` : "";
    return {
      text: `${acked}Raise the City's bill from this quotation.`,
      step: "boq",
      action: { kind: "raise-boq", label: "Raise the customer BOQ" },
      done: false,
    };
  }
  const open: NextAction = { kind: "goto", label: "Open the bill", step: "boq" };
  const read: NextAction = { kind: "goto", label: "Read the City's PO", step: "customer", read: true };
  if (boq.status === "draft" || boq.status === "generated") {
    return { text: `${boq.name} is a draft. Check its lines and issue it to the City.`, step: "boq", action: open, done: false };
  }
  const customer = detail.customer_order ?? null;
  if (customer !== null && (customer.status === "rejected" || customer.status === "cancelled")) {
    return {
      text: `The City's PO ${customer.number} was ${customer.status}. Read the City's corrected PO when it arrives.`,
      step: "customer",
      action: read,
      done: false,
    };
  }
  if (customer !== null && customer.status !== "validated" && customer.status !== "closed") {
    return {
      text: `The City's PO ${customer.number} is recorded but not yet checked against ${boq.name}. Check it, then issue the invoice.`,
      step: "customer",
      action: { kind: "goto", label: "Check the City's PO", step: "customer" },
      done: false,
    };
  }
  if (boq.status === "submitted") {
    return {
      text: `${boq.name} has been issued to the City. Read the City's PO when it arrives.`,
      step: "customer",
      action: read,
      done: false,
    };
  }
  if (boq.status !== "approved") {
    return { text: `${boq.name} is ${boqLabel(boq.status).toLowerCase()}.`, step: "boq", action: open, done: false };
  }
  if (customer === null) {
    // Approved before the City's PO approved bills: read the PO, then bill.
    return {
      text: "The bill is approved. Read the City's PO when it arrives, then issue the invoice.",
      step: "customer",
      action: read,
      done: false,
    };
  }
  return {
    text: `The City's PO ${customer.number} is validated against ${boq.name}. Issue the tax invoice.`,
    step: "invoice",
    action: { kind: "issue-invoice", label: "Issue the tax invoice", boqId: boq.id },
    done: false,
  };
}

function finished(detail: QuotationDetail): NextStep {
  const invoice = detail.invoice ?? null;
  if (invoice !== null) {
    const when = invoice.invoice_date ? ` on ${formatDate(invoice.invoice_date)}` : "";
    return { text: `Complete. Invoiced to the City as ${invoice.number}${when}.`, step: "invoice", action: null, done: true };
  }
  if (detail.status === "rejected") {
    const when = detail.decision?.at ? ` on ${formatDate(detail.decision.at)}` : "";
    return { text: `Rejected${when}. Nothing more happens to this quotation.`, step: "quote", action: null, done: true };
  }
  if (detail.status === "superseded") {
    return { text: "Superseded by a later quotation. Nothing more happens to this one.", step: "quote", action: null, done: true };
  }
  if (detail.supplier_order?.status === "cancelled") {
    return {
      text: `${detail.supplier_order.number} was cancelled. Nothing more happens on this job.`,
      step: "po",
      action: null,
      done: true,
    };
  }
  return { text: "Nothing is waiting on this job.", step: "quote", action: null, done: true };
}

/** The one thing to do next on this job, in a sentence, with the one button
 * that does it -- read from the queue the server put the job in, so the
 * banner and the rail always agree about where the job is. */
export function nextStep(detail: QuotationDetail): NextStep {
  switch (detail.queue) {
    case "capture":
      return capturing(detail);
    case "approve":
      return approving(detail);
    case "price":
      return pricing(detail);
    case "supplier": {
      const order = detail.supplier_order;
      if (order?.status === "issued") {
        return {
          text: `${order.number} is issued. Email it to ${supplierName(detail)}, then mark it sent.`,
          step: "po",
          action: { kind: "goto", label: "Mark it sent", step: "po" },
          done: false,
        };
      }
      const text = order
        ? `${order.number} is with ${supplierName(detail)}. Waiting for their acknowledgement.`
        : `The order is with ${supplierName(detail)}. Waiting for their acknowledgement.`;
      const canRaise = detail.boq === null && !detail.boq_blocker;
      return {
        text: canRaise ? `${text} The City's bill can be raised meanwhile.` : text,
        step: "po",
        action: canRaise ? { kind: "raise-boq", label: "Raise the customer BOQ" } : null,
        done: false,
      };
    }
    case "bill":
      return billing(detail);
    default:
      return finished(detail);
  }
}

/** The document a job opens on when no step is named in the address: where
 * the next thing happens for the office, the order once there is one for a
 * supplier. */
export function openingStep(detail: QuotationDetail, supplierView: boolean): StepKey {
  if (supplierView) {
    return detail.supplier_order !== null ? "po" : "quote";
  }
  return nextStep(detail).step;
}

/** A job's reference as the office says it: the supplier's account and their
 * own number, "SEA001-Q141", which is unique where the number alone is not. */
export function jobRef(supplier: { acc_no: string } | null, number: string): string {
  return supplier ? `${supplier.acc_no}-${number}` : number;
}

/** The address of one document on its job. Every link between the job's
 * documents goes here rather than to `/boq/:id` or `/invoices/:id`, which open
 * the document on its own and lose the job (Fix #7). */
export function jobLink(quotationId: number, step: StepKey): string {
  return `/commercial/${quotationId}?tab=${step}`;
}
